"use client";

import React, { forwardRef, useRef, useLayoutEffect } from "react";
import { AttachFile, Send, Stop } from "@mui/icons-material";
import { cn } from "@/lib/utils";
import { colors, spacing, radius, textStyles } from "@/ui/tokens";
import inputStyles from "./Input.module.css";
import { SOLA_ACCEPT_ATTRIBUTE } from "@/lib/sola/attachments/validate";

function InputButton({
  children,
  bg,
  color: textColor,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & {
  bg: string;
  color: string;
}) {
  return (
    <button
      {...props}
      style={{
        width: 40,
        height: 40,
        borderRadius: radius.pill,
        border: "none",
        backgroundColor: bg,
        color: textColor,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: props.disabled ? "not-allowed" : "pointer",
        flexShrink: 0,
        transition: "background-color 0.2s ease, color 0.2s ease",
      }}
    >
      {children}
    </button>
  );
}

interface InputProps {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onSubmit: (e: React.FormEvent) => void;
  disabled?: boolean;
  placeholder?: string;
  /**
   * Accepts both the SDK's chat status ("ready" | "streaming" | "submitted")
   * and the SOLA store's status ("idle" | "streaming" | "error"); only
   * "streaming"/"submitted" drive the loading UI.
   */
  status?: "ready" | "streaming" | "submitted" | "idle" | "error";
  onStop?: () => void;
  className?: string;
  /** Optional attachment handlers — when present, render the paperclip button + paste handling. */
  onAttach?: (files: File[]) => void;
  attachDisabled?: boolean;
  attachUploading?: boolean;
  hasAttachments?: boolean;
}

export const Input = forwardRef<HTMLTextAreaElement, InputProps>(
  (
    {
      value,
      onChange,
      onSubmit,
      disabled = false,
      placeholder = "Ask SOLA about recruiting, candidates, or market insights...",
      status = "ready",
      onStop,
      className,
      onAttach,
      attachDisabled = false,
      attachUploading = false,
      hasAttachments = false,
    },
    ref
  ) => {
    const isLoading = status === "streaming" || status === "submitted";
    const internalRef = useRef<HTMLTextAreaElement>(null);
    const textareaRef = (ref as React.RefObject<HTMLTextAreaElement>) || internalRef;
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleAttachClick = () => {
      fileInputRef.current?.click();
    };

    const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (files && files.length > 0 && onAttach) {
        onAttach(Array.from(files));
      }
      // Reset so re-selecting the same file fires onChange.
      e.target.value = "";
    };

    const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
      if (!onAttach) return;
      const files: File[] = [];
      for (const item of Array.from(e.clipboardData.items)) {
        if (item.kind === "file") {
          const file = item.getAsFile();
          if (file) files.push(file);
        }
      }
      if (files.length > 0) {
        e.preventDefault();
        onAttach(files);
      }
    };

    // Auto-resize textarea (useLayoutEffect prevents visible flash)
    useLayoutEffect(() => {
      const textarea = textareaRef.current;
      if (textarea) {
        textarea.style.height = "auto";
        textarea.style.height = `${Math.min(textarea.scrollHeight, 120)}px`;
      }
    }, [value, textareaRef]);

    return (
      <div className={cn(inputStyles.inputContainer, className)}>
        <form
          onSubmit={onSubmit}
          style={{
            display: "flex",
            alignItems: "center",
            gap: spacing[2],
            padding: spacing[3],
            paddingBottom: 0,
          }}
        >
          {onAttach && (
            <>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept={SOLA_ACCEPT_ATTRIBUTE}
                hidden
                onChange={handleFileInputChange}
                aria-hidden
              />
              <InputButton
                type="button"
                onClick={handleAttachClick}
                disabled={attachDisabled || isLoading}
                bg={colors.canvas.card}
                color={
                  attachDisabled || isLoading
                    ? colors.text.disabled
                    : colors.text.muted
                }
                aria-label="Attach files"
              >
                <AttachFile style={{ fontSize: 20 }} />
              </InputButton>
            </>
          )}

          <textarea
            ref={textareaRef}
            value={value}
            onChange={onChange}
            disabled={disabled}
            placeholder={placeholder}
            rows={1}
            aria-label="Message input"
            className={inputStyles.textarea}
            onPaste={onAttach ? handlePaste : undefined}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                onSubmit(e);
              }
            }}
          />

          {isLoading && onStop ? (
            <InputButton
              type="button"
              onClick={onStop}
              bg={colors.signal.danger}
              color="white"
              aria-label="Stop generating"
            >
              <Stop style={{ fontSize: 20 }} />
            </InputButton>
          ) : (
            <InputButton
              type="submit"
              disabled={
                disabled ||
                isLoading ||
                attachUploading ||
                (!value.trim() && !hasAttachments)
              }
              bg={
                disabled ||
                isLoading ||
                attachUploading ||
                (!value.trim() && !hasAttachments)
                  ? colors.text.disabled
                  : colors.action.product
              }
              color={
                disabled ||
                isLoading ||
                attachUploading ||
                (!value.trim() && !hasAttachments)
                  ? colors.text.muted
                  : "white"
              }
              aria-label="Send message"
            >
              <Send style={{ fontSize: 20 }} />
            </InputButton>
          )}
        </form>
        {/* Keyboard hint - always rendered to prevent layout shift */}
        <div
          style={{
            paddingLeft: spacing[4],
            paddingBottom: spacing[2],
            visibility: !isLoading && value.trim() ? "visible" : "hidden",
          }}
        >
          <span style={{ ...textStyles.caption, color: colors.text.disabled, fontSize: 11 }}>
            Enter to send · Shift+Enter for new line
          </span>
        </div>
      </div>
    );
  }
);

Input.displayName = "Input";
